// src/App.js
import React, { useState, useEffect, useContext, useLayoutEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, Outlet } from 'react-router-dom';
import { event } from './utils/gtm';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { UserContext, UserProvider } from './context/UserContext';
import { loadStripe } from '@stripe/stripe-js';
import { API_BASE_URL, getAuthHeaders } from './utils/utils.js';
import AuroraBackground from './components/AuroraBackground';
import './App.css';
import DashboardLayout from './components/Layout';
import LoginPage from './components/LoginPage';
import KeywordResearchTool from './components/KeywordResearchTool';
import ChannelAnalyzerTool from './components/ChannelAnalyzerTool';
import ScriptIntelligenceTool from './components/ScriptIntelligenceTool';
import HomePage from './components/HomePage';
import PricingPage from './components/PricingPage';
import SuccessPage from './components/SuccessPage';
import Footer from './components/Footer';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import ScrollToTop from './components/ScrollToTop';
import AdminPage from './components/AdminPage';
import FeedbackReportPage from './components/FeedbackReportPage';
import GoogleAnalytics from './utils/GoogleAnalytics';
import CookieBanner from './utils/CookieBanner';
import { Tooltip } from 'react-tooltip';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const ACTION_COSTS = {
  initial_search: 1, // ~105 units
  analyze_serp: 3, // ~105 units
  generate_titles: 2, // AI call
  analyze_channel: 3, // ~215 units
  analyze_script: 5, // Intensive AI call
  regenerate_script_titles: 2 // Focused AI call
};

// The "Guard" component that protects routes
const ProtectedRoute = ({ children }) => {
  const { user } = useContext(UserContext);
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// A new "Guard" component specifically for admin routes
const AdminRoute = ({ children }) => {
  const { user } = useContext(UserContext);

  if (!user || user.is_admin !== true) { // Explicitly check if is_admin is NOT the boolean true
    return <Navigate to="/" replace />;
  }
  return children;
};

function AppLogic() {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  // GTM pageview tracking for SPA navigations
  useEffect(() => {
    // This logic is now handled by GoogleAnalytics.js for the initial load
    // and by GTM's history change trigger for subsequent SPA navigations.
    // If you need manual control, you would check consent and call pageview() here.
  }, [location]);

  // This effect will run every time the page location changes.
  // It updates the document title to reflect the current page.
  useEffect(() => {
    const path = location.pathname;
    let title;
    switch (path) {
      case '/':
        title = 'TubeTactix | AI-Powered YouTube Strategy Assistant';
        break;
      case '/keyword-research':
        title = 'Keyword Research - TubeTactix';
        break;
      case '/channel-analyzer':
        title = 'Channel Analyzer - TubeTactix';
        break;
      case '/script-intelligence':
        title = 'Script Intelligence - TubeTactix';
        break;
      case '/pricing':
        title = 'Pricing - TubeTactix';
        break;
      case '/login':
        title = 'Login - TubeTactix';
        break;
      default:
        title = 'TubeTactix';
    }
    document.title = title;
  }, [location]);

  const [keyword, setKeyword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState("0");
  const [keywordResults, setKeywordResults] = useState([]);
  const [isKeywordLoading, setIsKeywordLoading] = useState(false);
  const [keywordError, setKeywordError] = useState('');

  const [channelInput, setChannelInput] = useState('');
  const [channelAnalysisResult, setChannelAnalysisResult] = useState(null);
  const [isChannelLoading, setIsChannelLoading] = useState(false);
  const [channelError, setChannelError] = useState('');

  const [competitors, setCompetitors] = useState([]);

  const [script, setScript] = useState('');
  const [scriptAnalysis, setScriptAnalysis] = useState(null);
  const [isScriptLoading, setIsScriptLoading] = useState(false);
  const [isRegeneratingTitles, setIsRegeneratingTitles] = useState(false);
  const [scriptError, setScriptError] = useState('');
  const [scriptAnalysesHistory, setScriptAnalysesHistory] = useState([]);

  const [loginError, setLoginError] = useState('');

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [sessionActionCount, setSessionActionCount] = useState(0);

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const triggerFeedbackModal = useCallback(() => {
    const lastPromptTime = localStorage.getItem('lastFeedbackPromptTime');
    const thirtyDays = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

    if (lastPromptTime && (Date.now() - parseInt(lastPromptTime, 10)) < thirtyDays) {
      return; // Cooldown period is active, do nothing.
    }

    setIsFeedbackModalOpen(true);
  }, []);

  const closeFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
  };

  const onFeedbackModalClose = () => {
    // Set timestamp on close to respect the user's choice to dismiss the prompt.
    localStorage.setItem('lastFeedbackPromptTime', Date.now().toString());
  };
  
  // Effect to trigger feedback modal for power users
  useLayoutEffect(() => {
    // Trigger on the 5th action.
    if (sessionActionCount === 5) {
      triggerFeedbackModal();
    }
  }, [sessionActionCount, triggerFeedbackModal]); // Dependency array is correct

  const handleManualFeedbackOpen = () => {
    // When user clicks the button, set the cooldown and open the modal.
    localStorage.setItem('lastFeedbackPromptTime', Date.now().toString());
    setIsFeedbackModalOpen(true);
  };


  const handleToolChange = (tool) => {
    navigate(tool);
    setIsMobileNavOpen(false);
  };
  
  // --- THIS FUNCTION NEEDS TO EXIST HERE ---
  const goToHome = () => {
    navigate('/');
    setIsMobileNavOpen(false);
  };

  useEffect(() => {
    // This function runs on the initial app load
    const verifySessionAndLoadData = async () => {
      const token = localStorage.getItem('authToken');
      let currentUser = null;

      if (token) {
        try {
          const res = await fetch(`${API_BASE_URL}/verify-token`, {
            method: 'POST',
            headers: getAuthHeaders(),
            // Send the token AND the timezone 
            body: JSON.stringify({ token, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone })
          });
          if (res.ok) {
            const data = await res.json();
            setUser(data.user);
            currentUser = data.user; // Keep track for initial data fetch
          } else {
            localStorage.removeItem('authToken'); // Clean up invalid token
            setUser(null);
          }
        } catch (error) {
          setUser(null);
        }
      }
      
      // If we verified a user, fetch their data immediately.
      // We pass the token to ensure we're using the most current one.
      if (currentUser) {
        fetchInitialData(token);
      }

      setIsInitialLoading(false); // Stop the main loading spinner
    };

    verifySessionAndLoadData();
  }, [setUser]); // This effect runs only once when the app mounts

  // This is the new helper function for fetching data
  const fetchInitialData = (token) => {
    const authHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    Promise.all([
      fetch(`${API_BASE_URL}/competitors`, { headers: authHeaders }),
      fetch(`${API_BASE_URL}/script-analyses`, { headers: authHeaders })
    ])
    .then(async ([competitorsRes, analysesRes]) => {
      if (competitorsRes.ok) setCompetitors(await competitorsRes.json());
      if (analysesRes.ok) setScriptAnalysesHistory(await analysesRes.json());
    })
    .catch(error => console.error("Error fetching initial data:", error));
  };

  const addCompetitor = (channelData) => {
    fetch(`${API_BASE_URL}/competitors`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        id: channelData.channel_id,
        title: channelData.title,
        thumbnail_url: channelData.thumbnail_url
      })
    })
    .then(res => res.json())
    .then(newCompetitor => {
      setCompetitors([...competitors, newCompetitor]);
    });
  };

  const removeCompetitor = (channelId) => {
    event({ action: 'manage_item', category: 'Engagement', label: 'Channel Analyzer: Remove Competitor' });
    fetch(`${API_BASE_URL}/competitors/${channelId}`, { method: 'DELETE', headers: getAuthHeaders() })
      .then(() => {
        setCompetitors(competitors.filter(c => c.id !== channelId));
      });
  };

  const handleAnalyzeScript = () => {
    event({ action: 'use_tool', category: 'Engagement', label: 'Script Intelligence: Analyze Script' });
    setSessionActionCount(prevCount => prevCount + 1);
    if (script.length < 50) { setScriptError('Please enter a script of at least 50 characters.'); return; }
    setIsScriptLoading(true);
    setScriptAnalysis(null);
    setScriptError('');

    fetch(`${API_BASE_URL}/analyze-script`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ script }) })
    .then(res => {
      if (res.status === 429) {
        throw new Error("You are making requests too quickly. Please wait a moment.");
      }
      if (res.status === 402) { // 402 Payment Required
        throw new Error("You have run out of daily credits. Please try again tomorrow.");
      }
      if (!res.ok) {
        throw new Error("Failed to fetch data.");
      }
      return res.json();
    })
    .then(data => {
      setScriptError('');

      if (data.error) { setScriptError(data.error); } 
      else {
        // The backend now returns the full analysis object including its new ID
        setScriptAnalysis(data.analysis); 
        // Wait 30 seconds before showing the feedback modal to let the user see the results.
        setTimeout(() => {
          triggerFeedbackModal();
        }, 30000);
        if (data.user) {
          setUser(data.user);
        }
        // Create a preview for the new history item on the frontend
        const script_preview = (script.length > 150) ? script.substring(0, 150) + '...' : script;
        const newHistoryItem = { ...data.analysis, script_preview: script_preview, full_script: script };
        setScriptAnalysesHistory([newHistoryItem, ...scriptAnalysesHistory]);
      }
      setIsScriptLoading(false);
    })
    .catch(err => { setScriptError(err.message); setIsScriptLoading(false); });
  };

  const deleteAnalysis = (analysisId) => {
    event({ action: 'manage_item', category: 'Engagement', label: 'Script Intelligence: Delete Script' });
    fetch(`${API_BASE_URL}/script-analyses/${analysisId}`, { method: 'DELETE', headers: getAuthHeaders() })
      .then(() => {
        setScriptAnalysesHistory(scriptAnalysesHistory.filter(a => a.id !== analysisId));
        if (scriptAnalysis && scriptAnalysis.id === analysisId) { setScriptAnalysis(null); setScript(''); }
      });
  };

  const loadAnalysisFromHistory = (historyItem) => {
    event({ action: 'use_tool', category: 'Engagement', label: 'Script Intelligence: Load From History' });
    setScriptAnalysis(historyItem.analysis);
    setScript(historyItem.full_script); 
  };

  const handleSearch = () => {
    event({ action: 'use_tool', category: 'Engagement', label: 'Keyword Research: Initial Search' });
    setSessionActionCount(prevCount => prevCount + 1);
    if (!keyword) { setKeywordError('Please enter a keyword.'); return; }
    setIsKeywordLoading(true);
    setKeywordResults([]);
    setKeywordError('');

    fetch(`${API_BASE_URL}/keyword-research/analyze`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        keyword: keyword,
        category_id: selectedCategory // This was missing
      })
    })
      .then(res => {
        if (res.status === 429) {
          throw new Error("You are making requests too quickly. Please wait a moment.");
        }
        if (res.status === 402) { // 402 Payment Required
          throw new Error("You have run out of daily credits. Please try again tomorrow.");
        }
        if (!res.ok) {
          throw new Error("Failed to analyze channel.");
        }
        return res.json();
      })
      .then(data => {
        setKeywordError('');

        if (data.error) { setKeywordError(data.error); } 
        else {
          const initialResults = data.keywords.map(kw => ({
            keyword: kw, analysis: null, // Start with no analysis data
            isLoading: false, isSerpVisible: false,
            titleIdeas: [],
            isGeneratingTitles: false
          }));
          setKeywordResults(initialResults);
          if (data.user) {
            setUser(data.user);
          }
        }
        setIsKeywordLoading(false);
      })
      .catch(err => { setKeywordError(err.message); setIsKeywordLoading(false); });
  };

  const handleGenerateTitles = (keyword, competitorTitles, insight, angle) => {
    event({ action: 'use_tool_pro', category: 'Engagement', label: 'Keyword Research: Generate Title Ideas' });
    setSessionActionCount(prevCount => prevCount + 1);
    setKeywordResults(prev => 
      prev.map(item => 
        item.keyword === keyword ? { ...item, isGeneratingTitles: true, titleIdeas: [] } : item
      )
    );

    fetch(`${API_BASE_URL}/generate-titles`, {
      method: 'POST',
      headers: getAuthHeaders(), // Use our secure headers
      body: JSON.stringify({
        keyword: keyword,
        competitor_titles: competitorTitles,
        strategic_insight: insight,
        angle_to_beat: angle,
      })
    })
    .then(res => {
      if (res.status === 429) {
        throw new Error("You are making requests too quickly. Please wait a moment.");
      }
      if (res.status === 402) { throw new Error("You have run out of daily credits. Please try again tomorrow."); }
      if (!res.ok) { throw new Error("AI Title generation failed."); }
      return res.json();
    })
    .then(data => {
      setKeywordResults(prev => 
        prev.map(item => 
          item.keyword === keyword ? { ...item, titleIdeas: data.title_ideas, isGeneratingTitles: false } : item
        )
      );
      if (data.user) {
        setUser(data.user);
      }
    })
    .catch(err => {
      setKeywordError(err.message); 
      setKeywordResults(prev => 
        prev.map(item => 
          item.keyword === keyword ? { ...item, isGeneratingTitles: false } : item
        )
      );
    });
  };

  const handleRegenerateScriptTitles = () => {
    event({ action: 'use_tool', category: 'Engagement', label: 'Script Intelligence: Regenerate Titles' });
    setSessionActionCount(prevCount => prevCount + 1);
    setScriptError('');
    setIsRegeneratingTitles(true);

    fetch(`${API_BASE_URL}/regenerate-script-titles`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        script: script,
        existing_titles: scriptAnalysis.title_ideas,
        analysis_id: scriptAnalysis.id // Pass the ID of the current analysis
      })
    })
    .then(res => {
      if (res.status === 402) { throw new Error("You have run out of credits."); }
      if (!res.ok) { throw new Error("Failed to regenerate titles."); }
      return res.json();
    })
    .then(data => {
      if (data.error) {
        setScriptError(data.error);
      } else {
        // For Pro users, replace all titles.
        // For Free users, the API returns one new title.
        let updatedTitles;
        if (user.plan === 'pro') {
          updatedTitles = data.new_title_ideas;
        } else {
          updatedTitles = [data.new_title_ideas[0], ...scriptAnalysis.title_ideas.slice(1)];
        }
        // Update the state for both the current view and the history list
        const updatedAnalysis = { ...scriptAnalysis, title_ideas: updatedTitles };
        setScriptAnalysis(updatedAnalysis);
        // Correctly update only the 'analysis' part of the history item
        setScriptAnalysesHistory(prevHistory => 
          prevHistory.map(item => 
            item.id === updatedAnalysis.id ? { ...item, analysis: updatedAnalysis } : item
        ));
        setUser(data.user); // Update user credits
      }      
    })
    .catch(err => { setScriptError(err.message); })
    .finally(() => setIsRegeneratingTitles(false));
  };

  const handleAnalyzeSerp = (index) => {
    event({ action: 'use_tool_pro', category: 'Engagement', label: 'Keyword Research: Analyze SERP' });
    setSessionActionCount(prevCount => prevCount + 1);
    const newResults = [...keywordResults];
    const currentItem = newResults[index];
    // If analysis data already exists, just toggle visibility
    if (currentItem.analysis) {
        currentItem.isSerpVisible = !currentItem.isSerpVisible;
        setKeywordResults(newResults);
        return;
    }
    currentItem.isLoading = true;
    setKeywordResults(newResults);

    fetch(`${API_BASE_URL}/analyze-serp/${selectedCategory}/${encodeURIComponent(currentItem.keyword)}`, {
      headers: getAuthHeaders()
    })
      .then(res => {
        if (res.status === 429) {
          throw new Error("You are making requests too quickly. Please wait a moment.");
        }
        if (res.status === 402) { // 402 Payment Required
          throw new Error("You have run out of daily credits. Please try again tomorrow.");
        }
        if (!res.ok) {
          throw new Error("Failed to fetch data.");
        }
        return res.json();
      })
      .then(data => {
        const finalResults = [...keywordResults];
        if (data.error) { console.error("SERP Analysis failed:", data.error); } 
        else {
            finalResults[index].analysis = data;
            finalResults[index].isSerpVisible = true;

            if (data.user) {
              setUser(data.user);
            }
        }
        finalResults[index].isLoading = false;
        setKeywordResults(finalResults);
      })
      .catch(err => {
        setKeywordError("SERP Analysis failed:", err);
        const finalResults = [...keywordResults];
        finalResults[index].isLoading = false;
        setKeywordResults(finalResults);
      });
  };

  const handleAnalyzeChannel = (channelIdOverride = null) => {
    event({ action: 'use_tool', category: 'Engagement', label: 'Channel Analyzer: Analyze Channel' });
    setSessionActionCount(prevCount => prevCount + 1);
    const inputToAnalyze = (typeof channelIdOverride === 'string') ? channelIdOverride : channelInput;
    if (!inputToAnalyze) { setChannelError('Please enter a channel URL, handle, or ID.'); return; }
    setIsChannelLoading(true);
    setChannelAnalysisResult(null);
    setChannelError('');

    fetch(`${API_BASE_URL}/analyze-channel`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ channel_input: inputToAnalyze })
    })
    .then(res => {
      if (res.status === 429) {
        throw new Error("You are making requests too quickly. Please wait a moment.");
      }
      if (res.status === 402) { // 402 Payment Required
        throw new Error("You have run out of daily credits. Please try again tomorrow.");
      }
      if (!res.ok) {
        throw new Error("Failed to analyze channel.");
      }
      return res.json();
    })
    .then(data => {
      setChannelError('');

      if (data.error) { setChannelError(data.error); } 
      else {
        setChannelAnalysisResult(data);
        if (data.user) {
          setUser(data.user);
        }
      }
      setIsChannelLoading(false);
    })
    .catch(err => { setChannelError(err.message); setIsChannelLoading(false); });
  };

  // function for the sidebar to trigger an analysis
  const analyzeSavedCompetitor = (competitor) => {
    navigate('/channel-analyzer');
    setChannelInput(competitor.title); // Pre-fill the input with the TITLE
    handleAnalyzeChannel(competitor.id);
  };

  const handleCheckout = async (priceId, mode) => {
    // This is a good place for these events, already implemented.
    if (mode === 'subscription') {
        event({ action: 'click_purchase', category: 'Monetization', label: 'Product: Pro Plan' });
      } else {
        event({ action: 'click_purchase', category: 'Monetization', label: 'Product: Credit Pack' });
      }
    try {
      // Make a POST request to the backend to create a checkout session
      const response = await fetch(`${API_BASE_URL}/create-checkout-session`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ price_id: priceId, mode }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session.');
      }

      const session = await response.json();

      // Get the Stripe instance and redirect to checkout
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({
        sessionId: session.sessionId,
      });

      if (error) {
        console.error('Stripe redirect error:', error.message);
      }
    } catch (error) {
      console.error('Checkout process error:', error);
    }
  };

  const handleLogin = (googleResponse) => {
    event({ action: 'login', category: 'Authentication', label: 'Sign in with Google' });

    setLoginError('');

    // Send the credential to our backend for verification
    fetch(`${API_BASE_URL}/auth/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ credential: googleResponse.credential, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone })
    })
    .then(async res => {
      if (!res.ok) {
        const errorData = await res.json(); // Try to get the error message from the backend
        // Use the specific message from the backend if it exists
        throw new Error(errorData.message || "Backend verification failed");
      }
      return res.json();
    })
    .then(data => {
      if (data.error) { throw new Error(data.error); }
      const { access_token, user } = data;
      localStorage.setItem('authToken', access_token);
      setUser(user);

      fetchInitialData(access_token);

      // Check for a redirect plan from the URL query
      const urlParams = new URLSearchParams(window.location.search);
      const plan = urlParams.get('plan');

      if (plan === 'pro' || plan === 'credits') {
        // If user intended to upgrade or buy, send them to the pricing page
        navigate('/pricing');
      } else {
        // Otherwise, send them to the homepage
        // navigate('/');
        window.location.href = '/';
      }
    })
    .catch(err => {
      console.error("Error during login process:", err)
      setLoginError(err.message);
    });
  };

  const handleLogout = () => {
    event({ action: 'logout', category: 'Authentication', label: 'Manual Logout' });
    // 1. Clear the user from the global state
    setUser(null);
    // 2. Remove the session token from local storage
    localStorage.removeItem('authToken');

    // 3. Clear all data that belonged to the previous user
    setCompetitors([]);
    setScriptAnalysesHistory([]);
    setKeywordResults([]);
    setChannelAnalysisResult(null);
    setScriptAnalysis(null);
    // Also reset any input fields
    setSelectedCategory("0"); // Reset the category dropdown
    setKeyword('');
    setChannelInput('');
    setScript('');
    // Reset all tool-specific ERROR states
    setKeywordError('');
    setChannelError('');
    setScriptError('');

    // 4. Redirect to the login page
    navigate('/login');
  };

  if (isInitialLoading) {
    return (
      <div className="initial-loading-container">
        <div className="loading-spinner"></div>
        <p>Preparing dashboard...</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage handleLogin={handleLogin} loginError={loginError} />} />
      <Route
        element={
          <div className="app-layout-container">
            <AuroraBackground />
            <DashboardLayout
              goToHome={goToHome}
              handleToolChange={handleToolChange}
              isMobileNavOpen={isMobileNavOpen}
              setIsMobileNavOpen={setIsMobileNavOpen}
              handleLogout={handleLogout}
              isFeedbackModalOpen={isFeedbackModalOpen}
              openFeedbackModal={handleManualFeedbackOpen}
              user={user} // Pass user object to layout for admin link rendering
              closeFeedbackModal={closeFeedbackModal}
              onFeedbackModalClose={onFeedbackModalClose}
            >
              <Outlet />
            </DashboardLayout>
            <Footer />
            <Tooltip 
              id="app-tooltip" 
              className="app-tooltip-popover"
              place="top"
              effect="solid"
            />
          </div>
        }
      >
        {/* Publicly accessible routes that use the layout */}
        <Route path="privacy" element={<PrivacyPolicyPage />} />
        <Route path="terms" element={<TermsOfServicePage />} />

        {/* All other pages are protected */}
        <Route element={<ProtectedRoute><Outlet/></ProtectedRoute>}>
          <Route index element={<HomePage user={user} setActiveTool={(tool) => navigate(`/${tool}`)} onCompetitorClick={analyzeSavedCompetitor} />} />
          <Route path="keyword-research" element={
            <KeywordResearchTool
              user={user}
              keyword={keyword}
              setKeyword={setKeyword}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              results={keywordResults}
              isLoading={isKeywordLoading}
              error={keywordError}
              handleSearch={handleSearch}
              handleAnalyzeSerp={handleAnalyzeSerp}
              onGenerateTitles={handleGenerateTitles} 
              actionCosts={ACTION_COSTS}
            />}
          />
          <Route path="channel-analyzer" element={
            <ChannelAnalyzerTool 
              user={user}
              channelInput={channelInput} setChannelInput={setChannelInput}
              analysisResult={channelAnalysisResult} isLoading={isChannelLoading}
              error={channelError} handleAnalyzeChannel={handleAnalyzeChannel}
              addCompetitor={addCompetitor}
              removeCompetitor={removeCompetitor}
              competitors={competitors}
              onCompetitorClick={analyzeSavedCompetitor}
              actionCosts={ACTION_COSTS}
            />} 
          />
          <Route path="script-intelligence" element={
            <ScriptIntelligenceTool 
              user={user}
              script={script} setScript={setScript}
              analysis={scriptAnalysis}
              isLoading={isScriptLoading}
              error={scriptError}
              handleAnalyze={handleAnalyzeScript}
              handleRegenerateTitles={handleRegenerateScriptTitles}
              isRegeneratingTitles={isRegeneratingTitles}
              history={scriptAnalysesHistory}
              deleteAnalysis={deleteAnalysis}
              loadAnalysisFromHistory={loadAnalysisFromHistory}
              actionCosts={ACTION_COSTS}
              triggerFeedbackModal={triggerFeedbackModal}
            />} 
          />        
          <Route path="pricing" element={
            <PricingPage 
              user={user}
              handleCheckout={handleCheckout}
            />
          } />
          <Route path="success" element={<SuccessPage />} />
          {/* Admin-only Route */}
          <Route path="admin" element={<AdminRoute><Outlet /></AdminRoute>}>
            {/* The main dashboard at /admin */}
            <Route index element={<AdminPage />} /> 
            {/* The specific report at /admin/feedback */}
            <Route path="feedback" element={<FeedbackReportPage />} />
          </Route>

        </Route>
      </Route>
      {/* A catch-all route for any other URL, must be last */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const App = () => (
  <BrowserRouter>
    <ScrollToTop />
    <GoogleAnalytics gtmId={process.env.REACT_APP_GTM_ID} />
    <UserProvider>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <AppLogic />
        <CookieBanner />
      </GoogleOAuthProvider>
    </UserProvider>
  </BrowserRouter>
);

export default App;